import * as React from 'react';
import { useCallback, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import isEqual from 'lodash/isEqual';

import GenericPlot from 'views/components/visualizations/GenericPlot';
import { Timestamp, Section, Select } from 'components/common';
import { adjustFormat, toUTCFromTz } from 'util/DateTime';
import { Button } from 'components/bootstrap';
import type {
  Evidences,
  EvidenceFilters,
  EvidenceTimeRangeFilter,
  Evidence,
} from 'security-app/components/Investigations/Details/TimelineSegment/types';
import {
  EVIDENCE_CAROUSEL_ID,
  evidenceTypeOptions,
} from 'security-app/components/Investigations/Details/TimelineSegment/Constants';
import TimelineHoverlabel from 'security-app/components/Investigations/Details/TimelineSegment/TimelineHoverlabel';
import useUserDateTime from 'hooks/useUserDateTime';
import { useSlidesInView } from 'components/common/Carousel';

import { evidenceTypeLabel } from './Utils';

const TimelineContainer = styled.div`
  height: 90px;
  margin-top: 25px;
  margin-bottom: 15px;
  position: relative;

  .hoverlayer {
    display: none;
  }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
`;

const Filter = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TypeSelect = styled(Select)`
  min-width: 90px;
`;

const Legend = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
`;

const LegendItem = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: ${theme.fonts.size.small};
  `,
);

const Marker = styled.div<{ $severity?: string }>(
  ({ $severity, theme }) => css`
    border-radius: 50%;
    width: 10px;
    height: 10px;
    background-color: ${$severity === 'high' ? theme.colors.severity.high : theme.colors.severity.low};
  `,
);

const MarkerBorder = styled(Marker)(
  ({ theme }) => css`
    border: 2px solid ${theme.colors.charts.highlight};
    background-color: transparent;
  `,
);

type Props = {
  evidences: Evidences;
  evidencesGroupedByTime: Array<[string, Array<Evidence>]>;
  filters: EvidenceFilters;
  initialTimelineRange: EvidenceTimeRangeFilter;
  onClickMarker: (evidenceTimeStamp: string) => void;
  setFilters: (newFilters: Partial<EvidenceFilters>) => void;
};

const chartDataCollection = ({
  markerColor,
  markerBorder,
  formatTime,
  evidences = [],
}: {
  markerColor: string;
  markerBorder?: unknown;
  formatTime: (dateTime: string, format: string) => string;
  evidences?: Evidences;
  mode?: 'markers' | 'lines+markers';
}) => ({
  type: 'scatter',
  name: '',
  x: evidences.map((evidence) => formatTime(evidence.timestamp, 'internal')),
  y: evidences.map(() => 1),
  mode: 'markers',
  marker: {
    color: markerColor,
    size: 20,
    line: markerBorder,
  },
});

const useChartData = (evidencesGroupedByTime: Array<[string, Array<Evidence>]>) => {
  const theme = useTheme();
  const { formatTime } = useUserDateTime();
  const groupsInView = useSlidesInView(EVIDENCE_CAROUSEL_ID, false);
  const evidenceInCarouselView = evidencesGroupedByTime
    .filter((_evidence, index) => groupsInView.includes(index))
    .flatMap((group) => group[1]);
  const evidencesNotInCarouselView = evidencesGroupedByTime
    .filter((_evidence, index) => !groupsInView.includes(index))
    .flatMap((group) => group[1]);

  return [
    chartDataCollection({
      markerColor: theme.colors.severity.low,
      formatTime,
      evidences: evidenceInCarouselView,
      markerBorder: {
        color: theme.colors.charts.highlight,
        width: 2,
      },
    }),
    chartDataCollection({
      markerColor: theme.colors.severity.low,
      formatTime,
      evidences: evidencesNotInCarouselView,
    }),
  ];
};

type HoverLabelType = {
  evidenceTimestamp: string;
  positionX: number;
  positionY: number;
};

const TimelineChart = ({
  evidences,
  evidencesGroupedByTime,
  filters,
  initialTimelineRange,
  setFilters,
  onClickMarker,
}: Props) => {
  const theme = useTheme();
  const isInitialRange = isEqual(initialTimelineRange, filters.timeRange);
  const chartData = useChartData(evidencesGroupedByTime);
  const [hoverLabel, setHoverLabel] = useState<HoverLabelType | null>(null);
  const { formatTime, userTimezone } = useUserDateTime();

  const setTimeRangeFilter = useCallback(
    (newTimeRange: EvidenceTimeRangeFilter) => setFilters({ timeRange: newTimeRange }),
    [setFilters],
  );
  const resetRange = useCallback(
    () => setTimeRangeFilter(initialTimelineRange),
    [initialTimelineRange, setTimeRangeFilter],
  );
  const resetSimilarFilter = useCallback(() => {
    setFilters({ similar: undefined });
  }, [setFilters]);

  const fromUserToInternalTime = useCallback(
    (dateTime: string) => adjustFormat(toUTCFromTz(dateTime, userTimezone), 'internal'),
    [userTimezone],
  );
  const hoverLabelEvidence = hoverLabel
    ? evidences.find(
        ({ timestamp }) =>
          adjustFormat(timestamp, 'internal') === adjustFormat(hoverLabel.evidenceTimestamp, 'internal'),
      )
    : null;

  return (
    <Section
      title="Timeline"
      actions={
        filters.timeRange && (
          <Filters>
            {filters.similar && (
              <Filter>
                Similar to selected {evidenceTypeLabel(filters.similar.targetEvidence.type, true)}{' '}
                <Button bsSize="xsmall" onClick={resetSimilarFilter}>
                  Reset
                </Button>
              </Filter>
            )}
            <Filter>
              Evidence type
              <TypeSelect
                options={evidenceTypeOptions}
                onChange={(newTypeFilter) => setFilters({ type: newTypeFilter })}
                value={filters.type}
                clearable={false}
                aria-label="Select a type"
                size="small"
              />
            </Filter>
            <Filter>
              <Timestamp dateTime={filters.timeRange.from} format="dateHourAndMinute" /> -{' '}
              <Timestamp dateTime={filters.timeRange.to} format="dateHourAndMinute" />
              <Button disabled={isInitialRange} bsSize="xs" onClick={resetRange}>
                Reset range
              </Button>
            </Filter>
          </Filters>
        )
      }>
      <TimelineContainer>
        <GenericPlot
          chartData={chartData}
          layout={{
            yaxis: {
              visible: false,
              fixedrange: true,
            },
            xaxis: {
              gridcolor: theme.colors.charts.lines,
              range: [formatTime(filters.timeRange.from, 'internal'), formatTime(filters.timeRange.to, 'internal')],
              type: 'date',
            },
          }}
          onZoom={(newFrom, newTo) => {
            setTimeRangeFilter({
              from: fromUserToInternalTime(newFrom),
              to: fromUserToInternalTime(newTo),
            });
          }}
          onHoverMarker={({ positionX, positionY, x }) => {
            setHoverLabel({
              positionX,
              positionY,
              evidenceTimestamp: fromUserToInternalTime(x),
            });
          }}
          onUnhoverMarker={() => setHoverLabel(null)}
          onClickMarker={({ x }) => onClickMarker(fromUserToInternalTime(x))}
        />
        {hoverLabel && hoverLabelEvidence && (
          <TimelineHoverlabel
            positionX={hoverLabel.positionX}
            positionY={hoverLabel.positionY}
            evidence={hoverLabelEvidence}
          />
        )}
      </TimelineContainer>
      <Legend>
        <LegendItem>
          <Marker /> Evidence
        </LegendItem>
        <LegendItem>
          <MarkerBorder /> Evidence card is in view
        </LegendItem>
      </Legend>
    </Section>
  );
};

export default TimelineChart;
